import { useState, useEffect } from "react";
import styles from "./DisableSubscriptionPlanForm.module.css";
import Button from "../../UI/Button/Button";
import FeedbackModal from "../../UI/FeedbackModal/FeedbackModal";
import {
  getSubscriptionPlans,
  updateSubscriptionPlan,
} from "../../../services/subscription-plan-service";

export default function DisableSubscriptionPlanForm() {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("info");
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setIsLoading(true);
    try {
      const result = await getSubscriptionPlans();
      if (result.success) {
        setPlans(result.data || []);
      } else {
        setModalType("error");
        setModalTitle("Error");
        setModalMessage(result.error || "Failed to load subscription plans");
        setModalOpen(true);
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Failed to load subscription plans");
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelect = (e) => {
    const planId = e.target.value;
    const plan = plans.find((p) => p._id === planId);
    setSelectedPlan(plan || null);
  };

  const handleToggleStatus = async () => {
    if (!selectedPlan) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Please select a subscription plan");
      setModalOpen(true);
      return;
    }

    if (selectedPlan.status === "active") {
      setModalType("error");
      setModalTitle("Cannot Disable Active Plan");
      setModalMessage(
        "Cannot disable an active subscription plan. Active plans must remain active while clients are paying. Please cancel the subscription through the payment system first.",
      );
      setModalOpen(true);
      return;
    }

    setIsUpdating(true);

    try {
      const newStatus =
        selectedPlan.status === "active" ? "inactive" : "active";
      const result = await updateSubscriptionPlan(selectedPlan._id, {
        status: newStatus,
      });

      if (result.success) {
        setModalType("success");
        setModalTitle("Success");
        setModalMessage(
          `Subscription plan "${selectedPlan.product || selectedPlan.projectId || selectedPlan._id}" status has been changed to ${newStatus}`,
        );
        setModalOpen(true);
        await loadPlans();

        const updatedPlan = plans.find((p) => p._id === selectedPlan._id);
        if (updatedPlan) {
          setSelectedPlan({ ...updatedPlan, status: newStatus });
        } else {
          setSelectedPlan({ ...selectedPlan, status: newStatus });
        }
      } else {
        throw new Error(
          result.error || "Failed to update subscription plan status",
        );
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(
        error.message || "Failed to update subscription plan status",
      );
      setModalOpen(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const isActive = selectedPlan?.status === "active";

  return (
    <>
      <div className={styles.projectForm}>
        <div className={styles.formGroup}>
          <label htmlFor="planSelect" className={styles.label}>
            Select Subscription Plan *
          </label>
          <select
            id="planSelect"
            name="planSelect"
            value={selectedPlan?._id || ""}
            onChange={handlePlanSelect}
            className={styles.select}
            required
            disabled={isLoading || isUpdating}
          >
            <option value="">
              {isLoading ? "Loading plans..." : "Select a subscription plan..."}
            </option>
            {plans.map((plan) => (
              <option key={plan._id} value={plan._id}>
                {plan.product || plan.projectId || plan._id} - {plan.status} (
                {plan.clientEmail})
                {plan.status === "active" ? " ⚠️ Cannot disable" : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedPlan && (
          <div className={styles.formGroup}>
            <div
              style={{
                padding: "16px",
                background: isActive ? "#d1fae5" : "#fef3c7",
                border: `1px solid ${isActive ? "#a7f3d0" : "#fde68a"}`,
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px 0",
                  color: isActive ? "#065f46" : "#92400e",
                  fontSize: "16px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background: isActive ? "#10b981" : "#f59e0b",
                  }}
                />
                Current Status: {isActive ? "Active" : "Inactive"}
              </h3>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Product:</strong> {selectedPlan.product || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Project ID:</strong> {selectedPlan.projectId || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Client Email:</strong>{" "}
                {selectedPlan.clientEmail || "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Tier:</strong>{" "}
                {selectedPlan.projectTier ||
                  selectedPlan.subscriptionPlan ||
                  "N/A"}
              </p>
              <p
                style={{ margin: "4px 0", color: "#374151", fontSize: "14px" }}
              >
                <strong>Amount:</strong> {selectedPlan.amount || "0"}{" "}
                {selectedPlan.currency || "RON"}
              </p>
              {isActive && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px",
                    background: "#fee2e2",
                    border: "1px solid #fecaca",
                    borderRadius: "6px",
                  }}
                >
                  <p
                    style={{
                      margin: "0",
                      color: "#991b1b",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    ⚠️ Warning: This plan is ACTIVE. Active plans cannot be
                    disabled to prevent interruption of client payments.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="default"
            onClick={handleToggleStatus}
            disabled={!selectedPlan || isUpdating || isLoading || isActive}
            style={{
              background: !isActive && selectedPlan ? "#10b981" : undefined,
              borderColor: !isActive && selectedPlan ? "#10b981" : undefined,
              opacity: isActive ? 0.5 : 1,
            }}
          >
            {isUpdating
              ? "Updating..."
              : isActive
                ? "Cannot Disable Active Plan"
                : "Set to Active"}
          </Button>
        </div>
      </div>

      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        confirmText="Close"
      />
    </>
  );
}
